import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const normalizeUser = (data) => {
  if (!data) return null;

  return {
    _id: data._id,
    name: data.name,
    email: data.email,
    phone: data.phone || '',
    address: data.address || '',
    role: data.role || 'user',
    avatar: data.avatar || '',
    isActive: data.isActive
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isAuthenticated = Boolean(user);
  const role = user?.role || null;
  const isAdmin = user?.role === 'admin';
  const isSubAdmin = user?.role === 'sub-admin';

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await api.get('/auth/me');
        const loggedInUser = normalizeUser(res);

        setUser(loggedInUser);
        localStorage.setItem('user', JSON.stringify(loggedInUser));
      } catch (err) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    setError('');

    try {
      const res = await api.post('/auth/login', { email, password });

      const token = res.token;
      const loggedInUser = normalizeUser(res);

      if (!token) {
        return { success: false, error: 'Token not received from server' };
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(loggedInUser));
      setUser(loggedInUser);

      return { success: true, user: loggedInUser };
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const signup = async (userData) => {
    setError('');

    try {
      const res = await api.post('/auth/register', userData);

      const token = res.token;
      const registeredUser = normalizeUser(res);

      if (!token) {
        return { success: false, error: 'Token not received from server' };
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(registeredUser));
      setUser(registeredUser);

      return { success: true, user: registeredUser };
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Signup failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/login';
  };

  const updateProfile = async (data) => {
    try {
      const res = await api.put('/auth/profile', data);
      const updatedUser = normalizeUser(res);

      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      return { success: true, user: updatedUser };
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Profile update failed';
      return { success: false, error: message };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        error,
        login,
        signup,
        logout,
        updateProfile,
        isAuthenticated,
        role,
        isAdmin,
        isSubAdmin
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;